"use client";
import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAuth } from "../../context/AuthContext";
import { useTaskBugs } from "../../context/TaskBugsContext";
import { useTaskContext } from "../../context/TaskContext";
import {
  LayoutDashboard,
  ListChecks,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Bug,
  ShieldAlert,
  RotateCcw,
} from "lucide-react";

// ── Status normalization (mirrors RequestManagement.jsx) ────────────────────
const STATUS_LABEL = {
  Open: "Open",
  Inprogress: "In Progress",
  Qa: "Ready For QA",
  Inreview: "QA In Progress",
  Deployment: "Pending Pre Prod Dep",
  ReadyForProduction: "Ready for Production",
  Fixed: "Done",
  Reopened: "Reopened",
  Closed: "Cancelled",
};

const STATUS_ORDER = [
  "Open",
  "Inprogress",
  "Deployment",
  "Qa",
  "Inreview",
  "ReadyForProduction",
  "Fixed",
  "Reopened",
  "Closed",
];

// CHANGED: build the lookup keyed on a normalized (trimmed, lowercased,
// spaces/underscores/hyphens stripped) version of every known alias, so
// backend variance like "open ", "IN_PROGRESS", "Ready for QA" etc. all
// resolve correctly instead of silently falling through.
const RAW_STATUS_ALIASES = {
  Open: "Open",
  Inprogress: "Inprogress",
  "In Progress": "Inprogress",
  Qa: "Qa",
  "Ready For QA": "Qa",
  Inreview: "Inreview",
  "In Review": "Inreview",
  "QA In Progress": "Inreview",
  Deployment: "Deployment",
  "Pending Pre Prod Dep": "Deployment",
  ReadyForProduction: "ReadyForProduction",
  "Ready for Production": "ReadyForProduction",
  Fixed: "Fixed",
  Done: "Fixed",
  Reopened: "Reopened",
  Closed: "Closed",
  Cancelled: "Closed",
};

const foldKey = (s) =>
  String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const STATUS_LOOKUP = Object.entries(RAW_STATUS_ALIASES).reduce((acc, [k, v]) => {
  acc[foldKey(k)] = v;
  return acc;
}, {});

const normalizeStatus = (s) => {
  if (s === null || s === undefined || String(s).trim() === "") return "Unknown"; // CHANGED: don't guess — surface it instead
  return STATUS_LOOKUP[foldKey(s)] || s;
};

const OPEN_REQUEST_STATUSES = ["Open", "Inprogress", "Deployment", "Qa", "Inreview", "Reopened"];

// CHANGED: same fold-key approach for task statuses.
const RAW_TASK_STATUS_ALIASES = {
  "To Do": "To Do",
  Todo: "To Do",
  Inprogress: "In Progress",
  "In Progress": "In Progress",
  Deployment: "Deployment",
  qa: "QA",
  Qa: "QA",
  QA: "QA",
  Inreview: "In Review",
  "In Review": "In Review",
  Completed: "Completed",
  Done: "Completed",
};

const TASK_STATUS_LOOKUP = Object.entries(RAW_TASK_STATUS_ALIASES).reduce((acc, [k, v]) => {
  acc[foldKey(k)] = v;
  return acc;
}, {});

const normalizeTaskStatus = (s) => {
  if (s === null || s === undefined || s === "") return "To Do";
  return TASK_STATUS_LOOKUP[foldKey(s)] || String(s);
};

// CHANGED: normalize bug "type" the same way — backend may send "cr",
// "CR", "Change Request", "change_request", etc. Previously `b.type ===
// "CR"` / `!== "CR"` was a strict, case-sensitive compare, so anything
// but the exact string "CR" silently got bucketed as a Bug.
const CR_ALIASES = new Set(["cr", "changerequest", "change"]);
const normalizeRequestType = (t) => {
  const folded = foldKey(t);
  return CR_ALIASES.has(folded) ? "CR" : "Bug";
};

const PIE_COLORS = ["#2563eb", "#d12465", "#16a34a", "#f59e0b", "#0891b2", "#7c3aed", "#dc2626", "#0d9488"];

// ── Reusable building blocks ─────────────────────────────────────────────────
function SummaryCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent} shadow-sm`}>
          <Icon size={20} className="text-white" />
        </div>
        {sub && (
          <span className="text-[11px] font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full">{sub}</span>
        )}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-800 leading-tight">{value}</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}>
        <Icon size={16} />
      </div>
      <div>
        <h2 className="text-base font-semibold text-gray-800">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
  );
}

function ChartCard({ title, legendItems, children, center }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>
      <div className="relative">
        {children}
        {center && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center -mt-2">
              <p className="text-2xl font-bold text-gray-800">{center.value}</p>
              <p className="text-[11px] text-gray-500">{center.label}</p>
            </div>
          </div>
        )}
      </div>
      {legendItems && (
        <div className="flex flex-wrap gap-2 mt-3">
          {legendItems.map((item, idx) => (
            <span
              key={item.name}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-600 bg-gray-50 px-2 py-1 rounded-full"
            >
              <span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[idx % PIE_COLORS.length] }} />
              {item.name} <span className="text-gray-400">({item.value})</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ProgressRow({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <span className="text-xs font-semibold text-gray-700">
          {value} <span className="text-gray-400">({pct}%)</span>
        </span>
      </div>
      <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-100 shadow-lg rounded-lg px-3 py-2 text-xs">
      {label && <p className="font-semibold text-gray-700 mb-1">{label}</p>}
      {payload.map((entry) => (
        <p key={entry.name} className="text-gray-600 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: entry.color || entry.fill }} />
          {entry.name}: <span className="font-semibold text-gray-800">{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function TasksRequestsDashboard() {
  const { user } = useAuth();
  const { bugs = [] } = useTaskBugs() || {};
  const { tasks = [] } = useTaskContext() || {};

  const role = (user?.role || "").toLowerCase();
  const isAllowed = role === "admin" || role === "hr";

  const requestStats = useMemo(() => {
    // CHANGED: normalize once per bug, reuse everywhere below, instead of
    // re-deriving status/type inside every separate .filter() (which is
    // how the counts drifted out of sync with each other).
    const normalizedBugs = bugs.map((b) => ({
      ...b,
      _status: normalizeStatus(b.status),
      _type: normalizeRequestType(b.type),
    }));

    // Dev aid: surface any status value we don't recognize so it can be
    // added to RAW_STATUS_ALIASES instead of silently mis-bucketing.
    if (process.env.NODE_ENV !== "production") {
      const unknown = new Set(
        normalizedBugs.filter((b) => !STATUS_ORDER.includes(b._status)).map((b) => b.status)
      );
      if (unknown.size) {
        console.warn("[TasksRequestsDashboard] Unrecognized bug status values:", [...unknown]);
      }
    }

    const total = normalizedBugs.length;
    // CHANGED: "Open Requests" now counts strictly status === "Open",
    // not the broader active-statuses bucket (Open + In Progress + QA +
    // Deployment + Reopened) that OPEN_REQUEST_STATUSES represented.
    const openCount = normalizedBugs.filter((b) => b._status === "Open").length;
    const activeCount = normalizedBugs.filter((b) => OPEN_REQUEST_STATUSES.includes(b._status)).length;
    const resolvedCount = normalizedBugs.filter((b) => b._status === "Fixed").length;
    const reopenedCount = normalizedBugs.filter((b) => b._status === "Reopened").length;
    const bugCount = normalizedBugs.filter((b) => b._type === "Bug").length;
    const crCount = normalizedBugs.filter((b) => b._type === "CR").length;

    const byStatus = STATUS_ORDER.map((key) => ({
      name: STATUS_LABEL[key],
      value: normalizedBugs.filter((b) => b._status === key).length,
    })).filter((row) => row.value > 0);

    const byType = [
      { name: "Bug", value: bugCount },
      { name: "Change Request", value: crCount },
    ].filter((row) => row.value > 0);

    const projectMap = {};
    normalizedBugs.forEach((b) => {
      const name = b.project_name || "Unassigned";
      projectMap[name] = (projectMap[name] || 0) + 1;
    });
    const byProject = Object.entries(projectMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    return { total, openCount, activeCount, resolvedCount, reopenedCount, byStatus, byType, byProject };
  }, [bugs]);

  const taskStats = useMemo(() => {
    // CHANGED: same single-normalization approach for tasks. Summary
    // cards now read off the same map that drives the pie chart, so they
    // can never disagree with each other again.
    const normalizedTasks = tasks.map((t) => ({
      ...t,
      _status: normalizeTaskStatus(t.status),
    }));

    if (process.env.NODE_ENV !== "production") {
      const known = new Set(Object.values(RAW_TASK_STATUS_ALIASES));
      const unknown = new Set(
        normalizedTasks.filter((t) => !known.has(t._status)).map((t) => t.status)
      );
      if (unknown.size) {
        console.warn("[TasksRequestsDashboard] Unrecognized task status values:", [...unknown]);
      }
    }

    const total = normalizedTasks.length;

    const statusMap = {};
    normalizedTasks.forEach((t) => {
      statusMap[t._status] = (statusMap[t._status] || 0) + 1;
    });
    const byStatus = Object.entries(statusMap).map(([name, value]) => ({ name, value }));

    const completedCount = statusMap["Completed"] || 0;
    const inProgressCount = statusMap["In Progress"] || 0;
    const todoCount = statusMap["To Do"] || 0;

    const priorityMap = {};
    normalizedTasks.forEach((t) => {
      const label = (t.priority || "Medium").trim();
      const normalized = label ? label.charAt(0).toUpperCase() + label.slice(1).toLowerCase() : "Medium";
      priorityMap[normalized] = (priorityMap[normalized] || 0) + 1;
    });
    const byPriority = Object.entries(priorityMap).map(([name, value]) => ({ name, value }));

    return { total, completedCount, inProgressCount, todoCount, byStatus, byPriority };
  }, [tasks]);

  if (!isAllowed) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-24 text-center px-4">
        <ShieldAlert size={48} className="text-gray-300 mb-3" />
        <h2 className="text-xl font-semibold text-gray-700">Access Restricted</h2>
        <p className="text-sm text-gray-500 mt-1 max-w-md">
          This dashboard is only available to Admin and HR users.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header banner */}
      <div
        className="rounded-2xl p-5 sm:p-6 mb-6 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
      >
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
            <LayoutDashboard size={24} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Tasks &amp; Requests Dashboard</h1>
            <p className="text-sm text-white/80 mt-0.5">
              High-level insights and analysis across tasks and requests &mdash; Admin &amp; HR only.
            </p>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <SummaryCard
          icon={ListChecks}
          label="Total Tasks"
          value={taskStats.total}
          accent="bg-gradient-to-br from-blue-500 to-indigo-600"
        />
        <SummaryCard
          icon={CheckCircle2}
          label="Completed Tasks"
          value={taskStats.completedCount}
          sub={taskStats.total ? `${Math.round((taskStats.completedCount / taskStats.total) * 100)}%` : "0%"}
          accent="bg-gradient-to-br from-green-500 to-emerald-600"
        />
        <SummaryCard
          icon={Loader2}
          label="Tasks In Progress"
          value={taskStats.inProgressCount}
          accent="bg-gradient-to-br from-orange-400 to-amber-600"
        />
        <SummaryCard
          icon={Bug}
          label="Total Requests"
          value={requestStats.total}
          accent="bg-gradient-to-br from-pink-500 to-rose-600"
        />
        <SummaryCard
          icon={AlertTriangle}
          label="Open Requests"
          value={requestStats.openCount}
          sub={requestStats.total ? `${Math.round((requestStats.openCount / requestStats.total) * 100)}%` : "0%"}
          accent="bg-gradient-to-br from-yellow-400 to-orange-500"
        />
        <SummaryCard
          icon={RotateCcw}
          label="Reopened Requests"
          value={requestStats.reopenedCount}
          accent="bg-gradient-to-br from-purple-500 to-violet-600"
        />
      </div>

      {/* Requests Overview */}
      <SectionHeader icon={Bug} title="Requests Overview" subtitle="Breakdown of bugs and change requests" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <ChartCard title="Requests by Status" legendItems={requestStats.byStatus} center={{ value: requestStats.total, label: "Total" }}>
          <ResponsiveContainer width="100%" height={240} debounce={200}>
            <PieChart>
              <Pie
                data={requestStats.byStatus}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
                isAnimationActive={false}
              >
                {requestStats.byStatus.map((entry, index) => (
                  <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="none" />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Requests by Type" legendItems={requestStats.byType} center={{ value: requestStats.total, label: "Total" }}>
          <ResponsiveContainer width="100%" height={240} debounce={200}>
            <PieChart>
              <Pie
                data={requestStats.byType}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
                isAnimationActive={false}
              >
                {requestStats.byType.map((entry, index) => (
                  <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="none" />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Requests Health</h3>
          <div className="space-y-4">
            <ProgressRow label="Resolved" value={requestStats.resolvedCount} total={requestStats.total} color="#16a34a" />
            <ProgressRow label="Open / In Progress" value={requestStats.activeCount} total={requestStats.total} color="#f59e0b" />
            <ProgressRow label="Reopened" value={requestStats.reopenedCount} total={requestStats.total} color="#7c3aed" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-6">
        <ChartCard title="Requests by Project">
         <ResponsiveContainer width="100%" height={280} debounce={200}>
  <BarChart data={requestStats.byProject} margin={{ left: -7, right: 8, top: 4, bottom: 0 }}>
    <CartesianGrid strokeDasharray="3 3" vertical={false} />
    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
    <YAxis allowDecimals={false} width={32} tick={{ fontSize: 11 }} />
    <Tooltip content={<CustomTooltip />} isAnimationActive={false} cursor={{ fill: "rgba(37, 99, 235, 0.06)" }} />
    <Bar dataKey="value" name="Requests" fill="#2563eb" radius={[6, 6, 0, 0]} isAnimationActive={false} />
  </BarChart>
</ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Tasks Overview */}
      <SectionHeader icon={ListChecks} title="Tasks Overview" subtitle="Distribution of tasks by status and priority" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Tasks by Status" legendItems={taskStats.byStatus} center={{ value: taskStats.total, label: "Total" }}>
          <ResponsiveContainer width="100%" height={240} debounce={200}>
            <PieChart>
              <Pie
                data={taskStats.byStatus}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
                isAnimationActive={false}
              >
                {taskStats.byStatus.map((entry, index) => (
                  <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="none" />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Tasks by Priority" legendItems={taskStats.byPriority} center={{ value: taskStats.total, label: "Total" }}>
          <ResponsiveContainer width="100%" height={240} debounce={200}>
            <PieChart>
              <Pie
                data={taskStats.byPriority}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
                isAnimationActive={false}
              >
                {taskStats.byPriority.map((entry, index) => (
                  <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="none" />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Tasks Health</h3>
          <div className="space-y-4">
            <ProgressRow label="Completed" value={taskStats.completedCount} total={taskStats.total} color="#16a34a" />
            <ProgressRow label="In Progress" value={taskStats.inProgressCount} total={taskStats.total} color="#f59e0b" />
            <ProgressRow label="To Do" value={taskStats.todoCount} total={taskStats.total} color="#2563eb" />
          </div>
        </div>
      </div>
    </div>
  );
}